import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { 
  LocationOn as LocationIcon, 
  People as PeopleIcon, 
  Settings as SettingsIcon, 
  AutoAwesome as AutoAwesomeIcon, 
  MenuBook as MenuBookIcon, 
  Timeline as TimelineIcon,
  Print as PrintIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Import the new editor components
import LocationEditor from './world-building/LocationEditor';
import CulturesEditor from './world-building/CulturesEditor';
import SystemsEditor from './world-building/SystemsEditor';
import FactionsEditor from './world-building/FactionsEditor';
import MagicTechEditor from './world-building/MagicTechEditor';
import LoreEditor from './world-building/LoreEditor';
import TimelineEditor from './world-building/TimelineEditor';

// Import the WorldBuildingReport component
import WorldBuildingReport from './world-building/WorldBuildingReport';
import { getWorldBuilding, saveWorldBuilding } from '../../services/storyToolsService';
import ProModal from '../common/ProModal';

// Default timeline settings
const DEFAULT_TIMELINE_SETTINGS = {
  startYear: 0,
  endYear: 1000,
  yearLabel: 'CE',
  displayYears: true
};

// Custom TabPanel component
const TabPanel = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`world-building-tabpanel-${index}`}
      aria-labelledby={`world-building-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const WorldBuilding = ({ projectId }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState(0);
  const [worldElements, setWorldElements] = useState({
    locations: [],
    cultures: [],
    systems: [],
    factions: [],
    magic: [],
    lore: [],
    timeline: { 
      events: [], 
      eras: [],
      settings: DEFAULT_TIMELINE_SETTINGS
    }
  });
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const saveTimerRef = useRef(null);

  // Save to API with debouncing and localStorage backup
  const saveToAPI = useCallback(async (elements) => {
    // Always save to localStorage immediately for offline access
    try {
      localStorage.setItem(`world-building-${projectId}`, JSON.stringify(elements));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    // Debounce API save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveWorldBuilding(projectId, {
          locations: elements.locations,
          cultures: elements.cultures,
          systems: elements.systems,
          factions: elements.factions,
          magic: elements.magic,
          lore: elements.lore,
          timeline: elements.timeline
        });
        console.log('✅ World building saved to API');
      } catch (error) {
        console.error('Error saving to API (localStorage backup active):', error);
      }
    }, 1500);
  }, [projectId]);

  // Load world building data from API with localStorage fallback
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await getWorldBuilding(projectId);
        if (response?.data?.worldBuilding) {
          const wb = response.data.worldBuilding;
          const loadedElements = {
            locations: wb.locations || [],
            cultures: wb.cultures || [],
            systems: wb.systems || [],
            factions: wb.factions || [],
            magic: wb.magic || [],
            lore: wb.lore || [],
            timeline: wb.timeline && wb.timeline.events ? wb.timeline : { 
              events: Array.isArray(wb.timeline) ? wb.timeline : [], 
              eras: wb.timeline?.eras || [],
              settings: wb.timeline?.settings || DEFAULT_TIMELINE_SETTINGS
            }
          };
          setWorldElements(loadedElements);
          console.log('✅ Loaded world building from API');
          
          // Update localStorage with API data
          localStorage.setItem(`world-building-${projectId}`, JSON.stringify(loadedElements));
        } else {
          throw new Error('No data from API');
        }
      } catch (error) {
        console.log('🔍 No API data, checking localStorage...');
        // Fallback to localStorage
        const savedElements = localStorage.getItem(`world-building-${projectId}`);
        if (savedElements) {
          try {
            const parsedElements = JSON.parse(savedElements);
            
            // Ensure timeline has settings
            if (parsedElements.timeline && !parsedElements.timeline.settings) {
              parsedElements.timeline.settings = DEFAULT_TIMELINE_SETTINGS;
            }
            
            setWorldElements(parsedElements);
            console.log('✅ Loaded world building from localStorage');
            
            // Migrate to API
            setTimeout(() => {
              saveWorldBuilding(projectId, {
                locations: parsedElements.locations || [],
                cultures: parsedElements.cultures || [],
                systems: parsedElements.systems || [],
                factions: parsedElements.factions || [],
                magic: parsedElements.magic || [],
                lore: parsedElements.lore || [],
                timeline: parsedElements.timeline
              })
                .then(() => console.log('✅ Migrated localStorage data to API'))
                .catch(err => console.log('Migration to API pending:', err.message));
            }, 2000);
          } catch (parseError) {
            console.error('Error parsing saved world elements:', parseError);
          }
        }
      }
    };
    
    loadData();
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [projectId]);

  // Category definitions
  const categories = [
    { name: 'locations', label: 'Locations', icon: <LocationIcon />, description: 'Physical places in your world' },
    { name: 'cultures', label: 'Cultures', icon: <PeopleIcon />, description: 'Societies, customs, and traditions' },
    { name: 'systems', label: 'Rules & Systems', icon: <SettingsIcon />, description: 'Laws, governments, and economics' },
    { name: 'factions', label: 'Factions', icon: <PeopleIcon />, description: 'Groups, organizations, and alliances' },
    { name: 'magic', label: 'Magic/Tech', icon: <AutoAwesomeIcon />, description: 'Supernatural or technological systems' },
    { name: 'lore', label: 'Lore', icon: <MenuBookIcon />, description: 'Myths, legends, and beliefs' },
    { name: 'timeline', label: 'Timeline', icon: <TimelineIcon />, description: 'Chronological events and eras' }
  ];

  // Handlers for locations
  const handleAddLocation = (location) => {
    const updatedLocations = [...worldElements.locations, location];
    const updatedElements = { ...worldElements, locations: updatedLocations };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleEditLocation = (location) => {
    const updatedLocations = worldElements.locations.map(item => 
      item.id === location.id ? location : item
    );
    const updatedElements = { ...worldElements, locations: updatedLocations };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleDeleteLocation = (locationId) => {
    const updatedLocations = worldElements.locations.filter(item => item.id !== locationId);
    const updatedElements = { ...worldElements, locations: updatedLocations };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  // Handlers for cultures
  const handleAddCulture = (culture) => {
    const updatedCultures = [...worldElements.cultures, culture];
    const updatedElements = { ...worldElements, cultures: updatedCultures };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleEditCulture = (culture) => {
    const updatedCultures = worldElements.cultures.map(item => 
      item.id === culture.id ? culture : item
    );
    const updatedElements = { ...worldElements, cultures: updatedCultures };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleDeleteCulture = (cultureId) => {
    const updatedCultures = worldElements.cultures.filter(item => item.id !== cultureId);
    const updatedElements = { ...worldElements, cultures: updatedCultures };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  // Handlers for systems
  const handleAddSystem = (system) => {
    const updatedSystems = [...worldElements.systems, system];
    const updatedElements = { ...worldElements, systems: updatedSystems };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleEditSystem = (system) => {
    const updatedSystems = worldElements.systems.map(item => 
      item.id === system.id ? system : item
    );
    const updatedElements = { ...worldElements, systems: updatedSystems };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleDeleteSystem = (systemId) => {
    const updatedSystems = worldElements.systems.filter(item => item.id !== systemId);
    const updatedElements = { ...worldElements, systems: updatedSystems };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  // Handlers for factions
  const handleAddFaction = (faction) => {
    const updatedFactions = [...worldElements.factions, faction];
    const updatedElements = { ...worldElements, factions: updatedFactions };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleEditFaction = (faction) => {
    const updatedFactions = worldElements.factions.map(item => 
      item.id === faction.id ? faction : item
    );
    const updatedElements = { ...worldElements, factions: updatedFactions };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleDeleteFaction = (factionId) => {
    const updatedFactions = worldElements.factions.filter(item => item.id !== factionId);
    const updatedElements = { ...worldElements, factions: updatedFactions };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  // Handlers for magic
  const handleAddMagic = (magic) => {
    const updatedMagic = [...worldElements.magic, magic];
    const updatedElements = { ...worldElements, magic: updatedMagic };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleEditMagic = (magic) => {
    const updatedMagic = worldElements.magic.map(item => 
      item.id === magic.id ? magic : item
    );
    const updatedElements = { ...worldElements, magic: updatedMagic };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleDeleteMagic = (magicId) => {
    const updatedMagic = worldElements.magic.filter(item => item.id !== magicId);
    const updatedElements = { ...worldElements, magic: updatedMagic };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  // Handlers for lore
  const handleAddLore = (lore) => {
    const updatedLore = [...worldElements.lore, lore];
    const updatedElements = { ...worldElements, lore: updatedLore };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleEditLore = (lore) => {
    const updatedLore = worldElements.lore.map(item => 
      item.id === lore.id ? lore : item
    );
    const updatedElements = { ...worldElements, lore: updatedLore };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  const handleDeleteLore = (loreId) => {
    const updatedLore = worldElements.lore.filter(item => item.id !== loreId);
    const updatedElements = { ...worldElements, lore: updatedLore };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  // Timeline event handlers
  const handleTimelineChange = (updatedTimeline) => {
    const updatedElements = {
      ...worldElements,
      timeline: updatedTimeline
    };
    setWorldElements(updatedElements);
    saveToAPI(updatedElements);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
          }}
        >
          <AutoAwesomeIcon sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            World Building
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Create locations, cultures, systems, and lore
          </Typography>
        </Box>
      </Box>
      
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3
        }}
      >
        <Box sx={{ flex: 1, bgcolor: isDarkMode ? 'transparent' : 'background.paper' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                height: '3px',
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                minHeight: '48px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }
            }}
          >
            {categories.map((category, index) => (
              <Tab 
                key={index} 
                icon={category.icon} 
                label={category.label}
                sx={{
                  '& .MuiTab-iconWrapper': {
                    marginBottom: '4px'
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>
        <Tooltip title="Print / Generate report" arrow>
          <span>
            <IconButton
              onClick={() => setOpenReportDialog(true)}
              aria-label="Generate world building report"
              sx={{
                width: 42,
                height: 42,
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.text.primary, isDarkMode ? 0.3 : 0.15)}`,
                color: theme.palette.text.primary,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.primary, isDarkMode ? 0.15 : 0.08)
                }
              }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Locations Tab */}
      <TabPanel value={activeTab} index={0}>
        <LocationEditor 
          locations={worldElements.locations} 
          onAddLocation={handleAddLocation}
          onEditLocation={handleEditLocation}
          onDeleteLocation={handleDeleteLocation}
        />
      </TabPanel>

      {/* Cultures Tab */}
      <TabPanel value={activeTab} index={1}>
        <CulturesEditor 
          cultures={worldElements.cultures} 
          onAddCulture={handleAddCulture}
          onEditCulture={handleEditCulture}
          onDeleteCulture={handleDeleteCulture}
        />
      </TabPanel>

      {/* Systems Tab */}
      <TabPanel value={activeTab} index={2}>
        <SystemsEditor 
          systems={worldElements.systems} 
          onAddSystem={handleAddSystem}
          onEditSystem={handleEditSystem}
          onDeleteSystem={handleDeleteSystem}
        />
      </TabPanel>

      {/* Factions Tab */}
      <TabPanel value={activeTab} index={3}>
        <FactionsEditor 
          factions={worldElements.factions} 
          onAddFaction={handleAddFaction}
          onEditFaction={handleEditFaction}
          onDeleteFaction={handleDeleteFaction}
        />
      </TabPanel>

      {/* Magic/Tech Tab */}
      <TabPanel value={activeTab} index={4}>
        <MagicTechEditor 
          magicSystems={worldElements.magic} 
          onAddMagic={handleAddMagic}
          onEditMagic={handleEditMagic}
          onDeleteMagic={handleDeleteMagic}
        />
      </TabPanel>

      {/* Lore Tab */}
      <TabPanel value={activeTab} index={5}>
        <LoreEditor 
          lore={worldElements.lore} 
          onAddLore={handleAddLore}
          onEditLore={handleEditLore}
          onDeleteLore={handleDeleteLore}
        />
      </TabPanel>

      {/* Timeline Tab */}
      <TabPanel value={activeTab} index={6}>
        <TimelineEditor 
          timeline={worldElements.timeline} 
          onTimelineChange={handleTimelineChange}
        />
      </TabPanel>

      <ProModal
        open={openReportDialog}
        onClose={() => setOpenReportDialog(false)}
        title="World Building Report"
        eyebrow="WORLD BUILDING"
        icon={<PrintIcon />}
        maxWidth="md"
        actions={(
          <Button 
            onClick={() => setOpenReportDialog(false)}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            Close
          </Button>
        )}
        contentProps={{ sx: { p: 0 } }}
      >
        <WorldBuildingReport 
          worldElements={worldElements} 
          projectId={projectId}
          onClose={() => setOpenReportDialog(false)}
        />
      </ProModal>
    </Box>
  );
};

export default WorldBuilding;
